import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../lib/utils';
import { Plus, Edit2, Trash2, Search, QrCode } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import ExportButton from '../components/ExportButton';
import { AssetFormDialog } from '../components/assets/AssetFormDialog';
import { AssetQrDialog } from '../components/assets/AssetQrDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  in_stock: 'bg-blue-100 text-blue-700',
  assigned: 'bg-emerald-100 text-emerald-700',
  active: 'bg-green-100 text-green-700',
  in_use: 'bg-green-100 text-green-700',
  under_maintenance: 'bg-amber-100 text-amber-700',
  under_repair: 'bg-amber-100 text-amber-700',
  damaged: 'bg-red-100 text-red-700',
  lost: 'bg-red-100 text-red-700',
  retired: 'bg-gray-100 text-gray-700',
  disposed: 'bg-gray-100 text-gray-700',
};

const PROGRAM_LABELS = {
  school: 'School',
  women_entrepreneurship: 'Women Entrepreneurship',
  administration: 'Administration',
};

const emptyForm = {
  asset_tag: '', qr_code: '', name: '', category: '', description: '',
  serial_number: '', model_number: '', brand: '', vendor: '',
  purchase_date: '', purchase_cost: '', funding_source: '', donor_grant: '',
  warranty_expiry: '', useful_life_years: '',
  program: '', department: '', assigned_to: '', location: '', date_assigned: '',
  status: 'in_stock', condition: 'good', notes: '',
};

const AssetsListPage = () => {
  const { hasRole } = useAuth();
  const canEdit = hasRole(['admin', 'finance_officer', 'project_manager', 'school_head']);

  const [meta, setMeta] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', category: '', program: '', location: '', status: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [qrAsset, setQrAsset] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/assets/meta`);
      setMeta(res.data);
    } catch (e) { toast.error('Failed to load asset metadata'); }
  }, []);

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await axios.get(`${API_URL}/api/assets?${params.toString()}`);
      setAssets(res.data);
    } catch (e) {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      ...emptyForm,
      ...a,
      purchase_cost: a.purchase_cost ?? '',
      useful_life_years: a.useful_life_years ?? '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        purchase_cost: form.purchase_cost ? parseFloat(form.purchase_cost) : 0,
        useful_life_years: form.useful_life_years ? parseInt(form.useful_life_years) : null,
      };
      if (editing) {
        await axios.put(`${API_URL}/api/assets/${editing.id}`, payload);
        toast.success('Asset updated');
      } else {
        await axios.post(`${API_URL}/api/assets`, payload);
        toast.success('Asset added');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ ...emptyForm });
      fetchAssets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/assets/${id}`);
      toast.success('Asset deleted');
      setDeleteTarget(null);
      fetchAssets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const allCategories = meta ? Object.values(meta.categories).flat() : [];
  const categoriesForProgram = meta && form.program === 'school' ? meta.categories.school
    : form.program === 'women_entrepreneurship' ? meta.categories.women_entrepreneurship
    : form.program === 'administration' ? meta.categories.operational
    : allCategories;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold">Asset Register</h2>
          <p className="text-muted-foreground">Track all organizational assets across locations and programs</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            path="exports/asset-register"
            filenameBase="suubi_asset_register"
            label="Export"
            testId="asset-export-btn"
          />
          {canEdit && (
            <Button onClick={openCreate} data-testid="add-asset-btn">
              <Plus className="mr-1 h-4 w-4" /> Register Asset
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Search by name, ID, tag, serial..."
                className="pl-9"
                data-testid="asset-search"
              />
            </div>
            <Select value={filters.program || 'all'} onValueChange={(v) => setFilters({ ...filters, program: v === 'all' ? '' : v })}>
              <SelectTrigger><SelectValue placeholder="Program" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Programs</SelectItem>
                {meta?.programs?.map(p => <SelectItem key={p} value={p}>{PROGRAM_LABELS[p] || p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.location || 'all'} onValueChange={(v) => setFilters({ ...filters, location: v === 'all' ? '' : v })}>
              <SelectTrigger><SelectValue placeholder="Location" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {meta?.locations?.map(l => <SelectItem key={l} value={l}>{l.replace(/_/g, ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
              <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {meta?.statuses?.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Asset table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Asset ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Program</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="w-28 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10">Loading...</TableCell></TableRow>
              ) : assets.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">No assets found</TableCell></TableRow>
              ) : (
                assets.map((a) => (
                  <TableRow key={a.id} className="table-row-hover" data-testid={`asset-row-${a.id}`}>
                    <TableCell className="font-mono text-xs">{a.asset_id}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{a.name}</p>
                        {a.serial_number && <p className="text-xs text-muted-foreground">SN: {a.serial_number}</p>}
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{a.category?.replace(/_/g, ' ')}</Badge></TableCell>
                    <TableCell>{a.program ? PROGRAM_LABELS[a.program] : '—'}</TableCell>
                    <TableCell className="capitalize">{a.location?.replace(/_/g, ' ') || '—'}</TableCell>
                    <TableCell>{a.assigned_to || '—'}</TableCell>
                    <TableCell><Badge className={STATUS_STYLES[a.status] || ''}>{a.status?.replace(/_/g, ' ')}</Badge></TableCell>
                    <TableCell className="text-right">{a.purchase_cost ? formatCurrency(a.purchase_cost) : '—'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setQrAsset(a)}
                          title="View / print QR code"
                          data-testid={`qr-asset-${a.id}`}
                        >
                          <QrCode className="h-4 w-4" />
                        </Button>
                        {canEdit && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => openEdit(a)} data-testid={`edit-asset-${a.id}`} className="h-8 px-2.5">
                              <Edit2 className="h-3.5 w-3.5 mr-1" />Edit
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setDeleteTarget(a)} data-testid={`delete-asset-${a.id}`} className="h-8 px-2.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive">
                              <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AssetFormDialog
        open={dialogOpen}
        onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditing(null); setForm({ ...emptyForm }); } }}
        editing={editing}
        form={form}
        setForm={setForm}
        onSubmit={handleSubmit}
        meta={meta}
        categories={categoriesForProgram}
      />

      <AssetQrDialog qrAsset={qrAsset} onClose={() => setQrAsset(null)} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-asset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete asset?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget ? (
                <>Delete <strong>{deleteTarget.name}</strong> ({deleteTarget.category})? This action cannot be undone.</>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && handleDelete(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-asset-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AssetsListPage;
